import {
  Component,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { register } from 'swiper/element/bundle';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateConfigService } from './services/translate-config.service';
import { GoogleMapsService } from './services/google-maps.service';
import { GeolocationService } from './services/geolocation.service';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge/dist/esm';
import { AuthService } from './services/auth.service';
import { Platform, LoadingController } from '@ionic/angular';
import { ChatMessagesService } from './services/chat-messages.service';
import { NotificationService } from './services/api/notification.service';
import { App } from '@capacitor/app';
import { combineLatest, Subscription } from 'rxjs';
import { DeepLinkService } from './services/deep-link.service';
import { AppPushNotificationService } from './services/app-push-notification.service';
import { isNativePushPlatform } from './services/utils/runtime-config.util';
import { SafeAreaService } from './services/safe-area.service';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private translateConfigService: TranslateConfigService,
    private googleMapsService: GoogleMapsService,
    private geolocationService: GeolocationService,
    private authService: AuthService,
    private platform: Platform,
    private chatMessagesService: ChatMessagesService,
    private notificationService: NotificationService,
    private deepLinkService: DeepLinkService,
    private loadingController: LoadingController,
    private pushNotificationService: AppPushNotificationService,
    private safeAreaService: SafeAreaService,
  ) {}

  // Suscripción para sincronizar el badge con los contadores
  private badgeSyncSub?: Subscription;

  isWeb = Capacitor.getPlatform() === 'web';
  disabled = signal(true);
  appReady = signal(false);
  showCookieBanner = signal(false);

  /**
   * Retorno del onboarding de Stripe (web): si la URL trae tryOpenApp=true,
   * intentamos reabrir la app nativa por deeplink cuanto antes, con independencia
   * de guards e interceptores. En el Custom Tab de Android la web NO está
   * autenticada, así que no podemos depender de cargar una ruta protegida
   * (p. ej. /rewards tiene AuthGuard y redirigiría a login).
   */
  private tryReopenNativeAppFromOnboarding(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tryOpenApp') !== 'true') {
        return;
      }
      const link = document.createElement('a');
      link.href = 'foundek://rewards';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);
    } catch (e) { /* noop */ }
  }

  async ngOnInit() {
    this.tryReopenNativeAppFromOnboarding();

    const runtimePlatform = Capacitor.getPlatform();

    // ── Safe-area: single entry point ──────────────────────────────────
    // 1. Body class first (before first paint) so CSS rules apply immediately
    this.safeAreaService.addNativeBodyClass(runtimePlatform);
    // 2. Read insets from native plugin & subscribe to changes
    await this.safeAreaService.initialize(runtimePlatform);
    // 3. Keyboard show/hide toggles .keyboard-open on body
    this.safeAreaService.setupKeyboardHandling(runtimePlatform);
    // ───────────────────────────────────────────────────────────────────

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const urls = ['/login', '/landing'];
        this.disabled.set(urls.includes(event.url));
        this.updateCookieBannerVisibility(event.urlAfterRedirects || event.url);
      });

    if (this.router.url) {
      this.updateCookieBannerVisibility(this.router.url);
    }

    // Aseguramos que se inicialice correctamente el servicio de traducción
    try {
      await this.translateConfigService.initLanguage();
    } finally {
      this.appReady.set(true);
    }

    this.platform.ready().then(async () => {
      if (isNativePushPlatform(Capacitor.getPlatform())) {
        await this.pushNotificationService.requestPermission();
      }
      this.authService.setupPushTopic();

      // Si el usuario está autenticado al arrancar, asegurar token FCM registrado
      if (this.authService.isAuthenticated()) {
        (this.authService as any).refreshAndRegisterFcmToken?.();
        (this.authService as any).listenTokenRefresh?.();
      }

      // Initialize deep links
      this.deepLinkService.initializeDeepLinks();

      // Warm native map and geolocation early to reduce first-view latency
      try {
        this.googleMapsService.preloadNativeMap().catch((err) => {});
      } catch (e) {}

      try {
        // Non-blocking geolocation warmup (will request permissions if needed)
        (this.geolocationService.getPositionWithPermissionHandling() as any).toPromise?.().catch?.(() => {});
      } catch (e) {}

      // Check if app was opened with a deep link
      const initialUrl = await this.deepLinkService.getInitialUrl();
      if (initialUrl) {
        // Give the app a moment to fully initialize before handling the deep link
        setTimeout(() => {
          this.deepLinkService.handleDeepLink({ url: initialUrl });
        }, 1000);
      }

    });

    this.platform.pause.subscribe(async () => {
      await this.pushNotificationService.setBadge(0);
      await Badge.clear();
    });
    this.platform.resize.subscribe(async () => {
      await this.pushNotificationService.setBadge(0);
      await Badge.clear();
    });

    // Precarga de la API de Google Maps
    try {
      try {
        await this.googleMapsService.loadGoogleMaps();
      } catch (error: any) {
      } finally {
      }
    } catch (e) {
      // fallback silencioso si falla el loading
      this.googleMapsService.loadGoogleMaps().catch((error: Error) => {
      });
    }

    // Cargar contador de notificaciones desde storage para inicializar UI
    this.notificationService.loadNotificationCountFromStorage();

    // Sincronizar badge del icono de la app con (chats no leídos + notificaciones no leídas)
    this.badgeSyncSub = combineLatest([
      this.chatMessagesService.getUnreadMessages(),
      this.notificationService.notificationCount$,
    ]).subscribe(async ([chatCount, notifCount]) => {
      const total =
        Math.max(0, Number(chatCount) || 0) +
        Math.max(0, Number(notifCount) || 0);
      // Solo establecer badge en plataformas móviles
      if (!this.platform.is('desktop')) {
        try {
          await this.pushNotificationService.setBadge(total);
        } catch {
          // badge update failed
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.badgeSyncSub) {
      this.badgeSyncSub.unsubscribe();
      this.badgeSyncSub = undefined;
    }
    this.safeAreaService.destroy();
  }

  updateCookieBannerVisibility(url: string | undefined): void {
    const currentPath = (url ?? '').split(/[?#]/)[0].replace(/\/+$/, '');
    this.showCookieBanner.set(currentPath === '/app/home');
  }
}
